import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@herit-fe/heritage-ui';
import { DataTable } from './data-table';

export interface GroupedColumnSub {
  key: string;
  header: string;
}

export interface GroupedColumn {
  key: string;
  /** 그룹 헤더(하위 컬럼들을 colSpan). */
  header: string;
  subColumns: GroupedColumnSub[];
}

interface GroupedTableProps<T> {
  /** 선두 고정 컬럼(rowSpan 2) — 예: 시간대. */
  leadHeader: string;
  leadRender: (row: T, index: number) => ReactNode;
  /** 그룹(2단 헤더) 컬럼들. */
  groups: GroupedColumn[];
  data: T[];
  rowKey: (row: T, index: number) => string | number;
  /** 그룹 sub-cell 값. (row, 그룹 index, subKey). */
  cell: (row: T, groupIndex: number, subKey: string) => ReactNode;
  /**
   * 고정높이 세로 스크롤(`DataTable scroll`, 기본 max-h `20rem`) + 2단 헤더 sticky + 선두열 sticky-left. MPPT/
   * 스트링처럼 행·열이 많은 표용(admin `PvTimeSeriesTable` 정합). 미지정 시 자연 높이(가로 스크롤만).
   */
  scroll?: boolean;
  /**
   * 세로 스크롤 높이 상한(`scroll` 일 때만). 반드시 **바운드 값**(px·rem·`vh` 등) — `'auto'`/미상한이면 뷰포트가
   * 스크롤 컨테이너가 안 돼 **2단 헤더 sticky 가 무력화**된다(sticky 는 overflow 스크롤 조상 기준). 기본 `'20rem'`.
   */
  maxHeight?: number | string;
}

/**
 * 2단(중첩) 헤더 테이블 — 공용 `DataTable` 토대 위 합성(admin `PvTimeSeriesTable` verbatim 정합). 그룹 헤더(예:
 * MPPT 01)가 하위 컬럼(전압/전류/전력)을 colSpan, 선두 컬럼(시간대)은 rowSpan. MPPT/스트링 등 "설비 × 측정항목 ×
 * 시간" 밀집 표의 SSOT. 외곽 카드 chrome·헤더 배경·셀 패딩은 `DataTable`(shell)이 소유하고, 컬럼 세로선은
 * `DataTable grid`(끝열 제외 border-r)가 전 컬럼에 그린다(admin `<DataTable scroll grid>` 정합). `scroll` 시
 * 고정높이 스크롤 + 2단 헤더 sticky: 1단(그룹)은 `top-0`, 2단(하위)은 1단 실제 높이를 런타임 측정(ResizeObserver)해
 * 그 아래에 붙인다(폰트·확대에 따라 오프셋이 달라져 고정 px 는 틀어짐, inline style 이 top-0 클래스를 이김).
 */
export function GroupedTable<T>({
  leadHeader,
  leadRender,
  groups,
  data,
  rowKey,
  cell,
  scroll = false,
  maxHeight = 'auto',
}: GroupedTableProps<T>) {
  // 2단 sticky 오프셋 — 1단(그룹) 행 높이를 측정해 2단 `top`(inline, DataTable 의 top-0 클래스를 이김)으로 준다.
  const groupRowRef = useRef<HTMLTableRowElement>(null);
  const [subHeaderTop, setSubHeaderTop] = useState(0);
  useLayoutEffect(() => {
    if (!scroll) return;
    const el = groupRowRef.current;
    if (!el) return;
    const measure = () => setSubHeaderTop(el.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [scroll]);

  return (
    <DataTable scroll={scroll} grid maxHeight={maxHeight}>
      <TableHeader>
        {/* 1단 — 시간(2행 병합) + 그룹(하위 수만큼 병합). scroll 시 행 높이를 측정해 2단 sticky top 오프셋으로 쓴다. */}
        <TableRow ref={scroll ? groupRowRef : undefined}>
          <TableHead
            rowSpan={2}
            className={scroll ? 'sticky left-0 top-0 z-30! bg-muted align-middle' : 'align-middle'}
          >
            {leadHeader}
          </TableHead>
          {groups.map((group) => (
            <TableHead key={group.key} colSpan={group.subColumns.length} className="text-center">
              {group.header}
            </TableHead>
          ))}
        </TableRow>
        {/* 2단 — 하위 헤더. scroll 시 sticky top = 측정한 1단 높이(inline)로 1단 바로 아래 고정. */}
        <TableRow>
          {groups.flatMap((group) =>
            group.subColumns.map((sub) => (
              <TableHead
                key={`${group.key}-${sub.key}`}
                className="whitespace-nowrap text-right"
                style={scroll ? { top: subHeaderTop } : undefined}
              >
                {sub.header}
              </TableHead>
            )),
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((row, ri) => (
          <TableRow key={rowKey(row, ri)}>
            <TableCell
              className={
                scroll
                  ? 'sticky left-0 z-10 whitespace-nowrap bg-card font-medium'
                  : 'whitespace-nowrap font-medium'
              }
            >
              {leadRender(row, ri)}
            </TableCell>
            {groups.flatMap((group, gi) =>
              group.subColumns.map((sub) => (
                <TableCell
                  key={`${group.key}-${sub.key}`}
                  className="whitespace-nowrap text-right tabular-nums"
                >
                  {cell(row, gi, sub.key)}
                </TableCell>
              )),
            )}
          </TableRow>
        ))}
      </TableBody>
    </DataTable>
  );
}
